import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { REQUEST_WIRE_SECRET } from '@app/interceptors/request-wire-secret';
import { EncryptionDecryption } from '@app/services/encryption-decryption/encryption-decryption';
import { Utility } from '@app/services/utility/utility';
import { environment } from '@environments/environment';
import { from, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

export const encryptionDecryptionInterceptor: HttpInterceptorFn = (req, next) => {
  if (!environment.encryption) {
    return next(req);
  }

  const wireService = inject(EncryptionDecryption);
  const utilityService = inject(Utility);

  const shouldPackWire =
    req.method !== 'GET' &&
    !!req.body &&
    !(req.body instanceof FormData) &&
    req.headers.get('Content-Type') === 'application/json';

  let wireSecret = req.context.get(REQUEST_WIRE_SECRET);

  const wireRequest$ = shouldPackWire
    ? from(wireService.packRequest(req.body)).pipe(
        switchMap(({ cipherPart, wireSecret: secret }) => {
          wireSecret = secret;
          const encryptedData = wireService.packWireBody(cipherPart, secret);
          return next(
            req.clone({
              body: { encryptedData },
              context: req.context.set(REQUEST_WIRE_SECRET, secret),
            }),
          );
        }),
      )
    : next(req);

  return wireRequest$.pipe(
    switchMap((event) => {
      if (
        event instanceof HttpResponse &&
        wireSecret &&
        utilityService.isEncryptedResponseBody(event.body)
      ) {
        return from(wireService.unpackResponse(event.body.data, wireSecret)).pipe(
          switchMap((body) => of(event.clone({ body }))),
          catchError(() => of(event)),
        );
      }
      return of(event);
    }),
    catchError((error: HttpErrorResponse) => {
      const errorBody = error.error;

      if (wireSecret && utilityService.isEncryptedResponseBody(errorBody)) {
        return from(wireService.unpackResponse(errorBody.data, wireSecret)).pipe(
          switchMap((body) => {
            const modifiedError = Object.assign(
              Object.create(Object.getPrototypeOf(error)),
              error,
              { error: body },
            ) as HttpErrorResponse;
            return throwError(() => modifiedError);
          }),
        );
      }
      return throwError(() => error);
    }),
  );
};
